<template>
  <teleport to="#modals">
    <div
      class="fixed inset-0 cursor-default overflow-y-auto"
      data-cy="modal"
      :class="modalClass"
    >
      <div class="fixed inset-0 z-10 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-black/75"></div>
      </div>
      <div class="absolute z-20 w-full">
        <div
          class="flex justify-center space-x-15 min-h-screen items-center px-4 pt-4 pb-20 text-center sm:p-0"
        >
          <div
            v-if="navigation?.left?.enabled"
            v-tooltip="navigation.left.label"
            class="ml-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-dim-grey self-center"
            data-cy="navigation-left"
            @click="$emit('navigate', 'previous')"
          >
            <icon-base :icon="IconArrowNavigationLeft" width="16" height="16" />
          </div>
          <div v-else-if="navigation" class="ml-10 w-10"></div>

          <div
            class="modal-base relative inline-block transform rounded-md bg-white text-left align-middle transition-all sm:my-8"
            :class="[
              {
                'w-11/12': sizeVal === 'xl',
                'w-7/10': sizeVal === 'large',
                'w-5/10': sizeVal === 'regular',
                'w-125': sizeVal === 'small',
                'w-100': sizeVal === 'xs',
              },
              navigation ? 'justify-self-center' : '',
            ]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
          >
            <div class="absolute -right-[60px] z-50" data-cy="modal-close-btn">
              <icon-base
                class="cursor-pointer"
                :icon="IconXLarge"
                width="44"
                height="44"
                @click="close"
              />
            </div>
            <div class="rounded-md bg-white sm:w-full">
              <div
                class="flex-column items-start border-gray-200 p-2 sm:p-6"
                :class="{ 'border-b': titleBorder }"
              >
                <div class="flex items-baseline space-x-5 pb-5">
                  <lf-h1
                    v-if="title"
                    id="modal-headline"
                    class="whitespace-pre-wrap"
                  >
                    {{ title }}
                  </lf-h1>
                  <slot name="type" />
                </div>
                <div class="-mb-6 flex">
                  <slot name="header"></slot>
                </div>
              </div>
              <slot name="content"></slot>
            </div>
          </div>

          <div
            v-if="navigation?.right?.enabled"
            v-tooltip="navigation.right.label"
            class="mr-10 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-dim-grey self-center"
            data-cy="navigation-right"
            @click="$emit('navigate', 'next')"
          >
            <icon-base
              class="rotate-180 transform"
              :icon="IconArrowNavigationLeft"
              width="16"
              height="16"
            />
          </div>
          <div v-else-if="navigation" class="mr-10 w-10"></div>
        </div>
      </div>
    </div>
  </teleport>
</template>
<script setup lang="ts">
import { type PropType, computed, onBeforeUnmount, onMounted } from "vue";

import IconArrowNavigationLeft from "@/components/icons/IconArrowNavigationLeft.vue";
import IconXLarge from "@/components/icons/deals/IconXLarge.vue";
import IconBase from "@/components/ui/IconBase.vue";

export type NavigateDirection = "previous" | "next";

export type NavigationArrow = { enabled: boolean; label: string };

export type NavigationConfig = {
  left?: NavigationArrow;
  right?: NavigationArrow;
};

defineEmits<{
  navigate: [direction: NavigateDirection];
}>();

const props = defineProps({
  title: {
    type: String,
    default: "",
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
  xs: {
    type: Boolean,
    default: false,
  },
  small: {
    type: Boolean,
    default: false,
  },
  large: {
    type: Boolean,
    default: false,
  },
  xl: {
    type: Boolean,
    default: false,
  },
  titleBorder: {
    type: Boolean,
    default: false,
  },
  modalClass: {
    type: String,
    default: "z-100",
  },
  navigation: {
    type: Object as PropType<NavigationConfig>,
    default: undefined,
  },
});

// TODO: Refactor the modal props to accept a single size prop
const sizeVal = computed(() => {
  if (props.xl) {
    return "xl";
  }
  if (props.large) {
    return "large";
  }
  if (props.small) {
    return "small";
  }
  if (props.xs) {
    return "xs";
  }
  return "regular";
});

const escapeCallback = (keyboardEvent: KeyboardEvent) => {
  if (keyboardEvent.code === "Escape" && props.close) {
    props.close();
  }
};

onMounted(() => {
  document.addEventListener("keydown", escapeCallback);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", escapeCallback);
});
</script>
